<template>
  <v-menu v-model="menu" :close-on-content-click="false" scroll-strategy="none">
    <!-- Activator -->
    <template v-slot:activator="{ props }">
      <v-text-field
        ref="treeSelect"
        v-bind="props"
        :label="label"
        readonly
        variant="underlined"
        hide-details
        clearable
        append-inner-icon="mdi-chevron-down"
        @click:clear="clearSelection"
        :class="{ 'menu-open': menu }"
        class="tree-select-v-select"
        :model-value="displayValue"
      >
      </v-text-field>
    </template>

    <!-- Dropdown content -->
    <v-card style="max-height: 400px; overflow-y: auto;">
      <v-treeview
        class="tree-select-treeview"
        :items="items"
        selectable
        select-strategy="classic"
        open-on-click
        item-value="id"
        item-title="name"
        item-children="children"
        v-model="selected"
        v-model:opened="open"
        expand-icon=""
        collapse-icon=""
      >
        <!-- Custom right-side arrow for nodes -->
        <template v-slot:title="{ item }">
          <div class="d-flex align-center justify-space-between w-100">
            <span>{{ item.name }}</span>
            <v-icon
              v-if="!isLeaf(item)"
              class="node-arrow"
              :class="{ open: open.includes(item.id) }"
            >
              mdi-chevron-down
            </v-icon>
          </div>
        </template>
      </v-treeview>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: "TreeSelect",
  emits: ["update:treeSelectedItems"],
  props: {
    value: {
      type: Array,
      default: () => [] // selected IDs
    },
    items: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      default: "Select items"
    }
  },
  data() {
    return {
      menu: false,
      open: [],
      selected: [...this.value] // local copy of v-model
    }
  },
  computed: {
    // Map of all items for convenience
    itemMap() {
      return this.buildMap(this.items)
    },
    displayValue() {
      return this.selectedFlattened.join(", ")
    },
    // Flattened selection with parent logic
    selectedFlattened() {
      const names = []

      const traverse = item => {
        if (!this.isLeaf(item)) {
          if (this.areAllChildrenSelected(item)) {
            names.push(item.name) // show parent if all children selected
          } else {
            item.children.forEach(child => traverse(child)) // recurse
          }
        } else {
          if (this.selected.includes(item.id)) {
            names.push(item.name)
          }
        }
      }

      this.items.forEach(item => traverse(item))
      return names
    }
  },
  watch: {
    selected(newVal) {
      this.$emit("update:treeSelectedItems", newVal) // send selected ids to parent
    },
    value(newVal) {
      this.selected = [...newVal] // keep local copy in sync
    },
    menu(isOpen) {
      // Blur the input when closing so label floats down
      if (!isOpen && this.$refs.treeSelect) {
        const inputEl = this.$refs.treeSelect.$el.querySelector("input")
        if (inputEl) {
          inputEl.blur()
        }
      }
    }
  },
  methods: {
    clearSelection() {
      this.selected = [] // clears all selected leaf IDs
      this.open = [] // close the tree
    },
    // To make nodes with an empty childrens array act like a leaf node
    isLeaf(item) {
      return !item.children || item.children.length === 0
    },
    // Checks recursively if all children of a node are selected
    areAllChildrenSelected(item) {
      if (this.isLeaf(item)) {
        return this.selected.includes(item.id)
      }
      return item.children.every(child => this.areAllChildrenSelected(child))
    },

    // Build lookup map of id -> {name, level, parentId}
    buildMap(items, level = 1, parent = null, map = {}) {
      items.forEach(item => {
        map[item.id] = {
          ...item,
          level,
          parentId: parent ? parent.id : null
        }

        if (!this.isLeaf(item)) {
          this.buildMap(item.children, level + 1, item, map)
        }
      })
      return map
    }
  }
}
</script>

<style lang="scss" scoped>
.tree-select-v-select :deep(.v-field__append-inner .v-icon) {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
}
.tree-select-v-select.menu-open :deep(.v-field__append-inner .v-icon) {
  transform: rotate(180deg);
}
.node-arrow {
  transition: transform 0.3s ease;
  transform-origin: center;
}
.node-arrow.open {
  transform: rotate(180deg);
}
.tree-select-treeview :deep(.v-list-item) {
  /* Selection is shown via the checkbox only, not a darkened row */
  --v-activated-opacity: 0;
}
</style>
